import { useRef } from "react";
import { useLibraryStore } from "../store/libraryStore";

interface MenuBarProps {
  onImport(files: File[]): void;
  onOpenMusicImport(): void;
  onOpenSettings(): void;
  onNewPlaylist(): void;
  onBatchEdit(): void;
  onExport(): void;
  onDelete(ids: string[]): void;
}

export function MenuBar({ onImport, onOpenMusicImport, onOpenSettings, onNewPlaylist, onBatchEdit, onExport, onDelete }: MenuBarProps) {
  const fileInput = useRef<HTMLInputElement>(null);
  const folderInput = useRef<HTMLInputElement>(null);
  const selected = useLibraryStore((state) => state.selectedIds);
  const enrich = useLibraryStore((state) => state.enrichTracks);

  return <div className="menu-bar" role="menubar" aria-label="Application menu" onClick={(event) => {
    if ((event.target as HTMLElement).closest("button")) {
      event.currentTarget.querySelectorAll("details[open]").forEach((details) => details.removeAttribute("open"));
    }
  }}>
    <div className="app-mark" aria-hidden="true">ND</div>
    <details className="menu">
      <summary>FILE</summary>
      <div className="menu-popup" role="menu">
        <button role="menuitem" onClick={() => fileInput.current?.click()}>Import files… <kbd>⌘O</kbd></button>
        <button role="menuitem" onClick={() => folderInput.current?.click()}>Import folder…</button>
        <button role="menuitem" onClick={onOpenMusicImport}>Music import / Spotify link…</button>
        <span className="menu-rule" />
        <button role="menuitem" onClick={onExport}>Export M3U8… <kbd>⌘E</kbd></button>
        <button role="menuitem" disabled={!selected.length} onClick={() => void useLibraryStore.getState().downloadTracks(selected)}>Download selected MP3s…</button>
      </div>
    </details>
    <details className="menu">
      <summary>EDIT</summary>
      <div className="menu-popup" role="menu">
        <button role="menuitem" disabled={!selected.length} onClick={onBatchEdit}>Batch edit… <kbd>⌘B</kbd></button>
        <button className="danger-menu-item" role="menuitem" disabled={!selected.length} onClick={() => onDelete(selected)}>Delete selected tracks… <kbd>⌫</kbd></button>
        <button role="menuitem" onClick={() => document.querySelector<HTMLInputElement>("#library-search")?.focus()}>Find… <kbd>⌘F</kbd></button>
        <span className="menu-rule" />
        <button role="menuitem" onClick={onOpenSettings}>Preferences…</button>
      </div>
    </details>
    <details className="menu">
      <summary>VIEW</summary>
      <div className="menu-popup" role="menu">
        <button role="menuitem" onClick={() => useLibraryStore.getState().setView({ view: "all" })}>All tracks</button>
        <button role="menuitem" onClick={() => useLibraryStore.getState().setView({ view: "missing" })}>Missing metadata</button>
        <button role="menuitem" onClick={() => useLibraryStore.getState().setView({ view: "duplicates" })}>Duplicates</button>
      </div>
    </details>
    <details className="menu">
      <summary>LIBRARY</summary>
      <div className="menu-popup" role="menu">
        <button role="menuitem" onClick={onNewPlaylist}>New playlist…</button>
        <button role="menuitem" disabled={!selected.length} onClick={() => void enrich(selected)}>Analyze + Auto Tag <kbd>⌘T</kbd></button>
      </div>
    </details>
    <details className="menu">
      <summary>HELP</summary>
      <div className="menu-popup help-copy" role="menu">
        <p><strong>Needle Drop Library 1.0</strong></p>
        <p>Private cloud record management.</p>
        <p>D1 metadata · private R2 audio · linked-folder sync.</p>
      </div>
    </details>
    <div className="menu-spacer" />
    <span className="mode-indicator">PRIVATE CLOUD</span>
    <input ref={fileInput} className="visually-hidden" type="file" accept="audio/mpeg,.mp3,application/zip,.zip" multiple onChange={(event) => onImport(Array.from(event.target.files ?? []))} />
    <input ref={folderInput} className="visually-hidden" type="file" accept="audio/mpeg,.mp3" multiple {...{ webkitdirectory: "" }} onChange={(event) => onImport(Array.from(event.target.files ?? []))} />
  </div>;
}
